import { BadgeCheck, Rocket, MessageCircle, ShieldCheck } from "lucide-react";
import { WHATSAPP_LINK, INDIHOME_LOGO } from "../constants/config";

const PROOF_POINTS = [
  { icon: BadgeCheck, text: "Sudah banyak yang berhasil" },
  { icon: MessageCircle, text: "Respon cepat, dibantu sampai selesai" },
  { icon: Rocket, text: "Proses cepat, tagihan langsung turun" },
];

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden pt-28 md:pt-40 pb-20 md:pb-32"
    >
      <div
        aria-hidden="true"
        className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#E60000]/5 pointer-events-none"
      />
      <div
        aria-hidden="true"
        className="absolute top-1/2 -left-52 w-[420px] h-[420px] rounded-full bg-[#E60000]/[0.04] pointer-events-none"
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <p
            data-testid="hero-eyebrow"
            className="animate-fade-up inline-flex items-center gap-2 text-xs md:text-sm font-semibold uppercase tracking-widest text-[#E60000] bg-red-50 border border-red-100 rounded-full px-4 py-2"
          >
            Melayani seluruh Indonesia — 100% online
          </p>

          <h1
            data-testid="hero-headline"
            className="animate-fade-up font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black text-gray-900 mt-6 leading-[1.05]"
            style={{ animationDelay: "0.1s" }}
          >
            Tagihan IndiHome Turun,
            <span className="block text-[#E60000]">Tanpa Ganti Provider.</span>
          </h1>

          <p
            data-testid="hero-subheadline"
            className="animate-fade-up text-base md:text-lg text-gray-600 leading-relaxed mt-6 max-w-xl"
            style={{ animationDelay: "0.2s" }}
          >
            RismaCell membantu migrasi paket IndiHome kamu ke{" "}
            <span className="font-semibold text-gray-900">paket retensi</span>{" "}
            yang harganya jauh lebih murah. Cukup chat, kami urus sampai
            selesai — di mana pun kamu berada.
          </p>

          <div
            className="animate-fade-up flex flex-col sm:flex-row gap-4 mt-10"
            style={{ animationDelay: "0.3s" }}
          >
            <a
              href="#order"
              data-testid="hero-order-button"
              className="inline-flex justify-center items-center bg-[#E60000] text-white font-semibold px-8 py-4 rounded-full text-base hover:bg-red-700 active:scale-95 transition-[background-color,transform] shadow-lg shadow-red-600/25"
            >
              Order Migrasi Sekarang
            </a>
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-button"
              className="inline-flex justify-center items-center gap-2 border-2 border-gray-900 text-gray-900 font-semibold px-8 py-4 rounded-full text-base hover:bg-gray-900 hover:text-white active:scale-95 transition-[background-color,color,transform]"
            >
              <MessageCircle className="w-5 h-5" />
              Tanya via WhatsApp
            </a>
          </div>

          <div
            data-testid="hero-safe-payment-banner"
            className="animate-fade-up mt-8 inline-flex items-center gap-4 bg-green-50 border-2 border-green-500 rounded-2xl px-6 py-4 shadow-md shadow-green-600/10"
            style={{ animationDelay: "0.35s" }}
          >
            <span className="w-11 h-11 rounded-full bg-green-600 flex items-center justify-center shrink-0">
              <ShieldCheck className="w-6 h-6 text-white" strokeWidth={2.25} />
            </span>
            <div>
              <p className="font-heading font-extrabold text-green-800 text-base md:text-lg leading-tight">
                Bayar Jasa SETELAH Migrasi Berhasil
              </p>
              <p className="text-xs md:text-sm text-green-700 font-medium mt-0.5">
                Tanpa biaya di muka — 100% aman, tidak berhasil tidak bayar
              </p>
            </div>
          </div>

          <ul
            data-testid="hero-proof-list"
            className="animate-fade-up mt-8 space-y-3"
            style={{ animationDelay: "0.4s" }}
          >
            {PROOF_POINTS.map((p) => (
              <li key={p.text} className="flex items-center gap-3">
                <p.icon
                  className="w-5 h-5 text-[#E60000] shrink-0"
                  strokeWidth={2.25}
                />
                <span className="text-sm md:text-base font-medium text-gray-700">
                  {p.text}
                </span>
              </li>
            ))}
          </ul>

          <div
            data-testid="hero-indihome-badge"
            className="animate-fade-up mt-10 inline-flex items-center gap-4 bg-white border border-black/10 rounded-2xl px-6 py-4 shadow-sm"
            style={{ animationDelay: "0.5s" }}
          >
            <img
              src={INDIHOME_LOGO}
              alt="IndiHome"
              className="h-7 md:h-8 w-auto"
              loading="lazy"
            />
            <span className="text-xs md:text-sm text-gray-500 font-medium max-w-[200px] leading-snug">
              Jasa migrasi paket retensi untuk pelanggan IndiHome
            </span>
          </div>
        </div>

        <div className="lg:col-span-5 relative">
          <div className="animate-fade-up relative" style={{ animationDelay: "0.25s" }}>
            <div
              aria-hidden="true"
              className="absolute -inset-4 bg-[#E60000] rounded-[2rem] rotate-3 opacity-10"
            />
            <img
              src="https://customer-assets-m6fa6gv7.emergentagent.net/job_hemat-indihome/artifacts/ea53231da05b8af9_IMG-20261018-WA0012.jpg"
              alt="Banner jasa migrasi paket IndiHome RismaCell dengan daftar harga paket retensi 1P 2P 3P"
              data-testid="hero-image"
              className="relative rounded-[2rem] w-full aspect-square object-cover shadow-2xl"
              loading="eager"
            />
            <div
              data-testid="hero-savings-badge"
              className="absolute -bottom-6 -left-4 md:-left-8 bg-white rounded-2xl shadow-xl border border-black/5 px-6 py-4"
            >
              <p className="font-heading font-extrabold text-2xl text-[#E60000] tracking-tight">
                Lebih Murah
              </p>
              <p className="text-xs text-gray-500 font-medium mt-0.5">
                dari tagihan bulanan biasanya
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
